import { useMenus } from '@/data/menuData';
import { ApiService } from '@/services/api.services';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FaFacebook, FaInstagram, FaYoutube } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export default function Footer() {
  const { menus, loading } = useMenus();
  const [services, setServices] = useState([]);

  /* =====================
   * Effects
   * ===================== */
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await ApiService.getServices();
        const data = res.data.filter((s) => s.is_active).sort((a, b) => a.position - b.position);

        setServices(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchServices();
  }, []);

  if (loading) return null;

  /* =====================
   * Derived Data
   * ===================== */
  const tentangKami = menus.find((m) => m.name === 'Tentang Kami')?.subMenu?.[0]?.items || [];

  /* =====================
   * Animations
   * ===================== */
  const socialItem = {
    hidden: { opacity: 0, y: 6 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: 'easeOut' },
    },
  };

  return (
    <footer className="relative text-slate-300 pb-5 pt-16">
      <div className="grid gap-10 md:grid-cols-3 lg:grid-cols-4">
        {/* LOGO */}
        <div>
          <img
            src="/logo/akr-logo-2.webp"
            alt="Koperasi AKR Syariah"
            className="h-8 xl:h-12 mb-6"
            loading="lazy"
          />

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-4 flex items-center justify-between max-w-[120px] lg:max-w-[150px]"
          >
            <motion.a
              variants={socialItem}
              href="https://www.facebook.com/akrsyariah/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="flex items-center justify-center rounded-full bg-white/80 hover:bg-blue-600 text-blue-600 hover:text-white w-8 h-8 sm:w-9 sm:h-9 transition-colors"
            >
              <FaFacebook className="text-sm sm:text-base" />
            </motion.a>
            <motion.a
              variants={socialItem}
              href="https://www.instagram.com/akrsyariah/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="flex items-center justify-center rounded-full bg-white/80 hover:bg-pink-600 text-pink-600 hover:text-white w-8 h-8 sm:w-9 sm:h-9 transition-colors"
            >
              <FaInstagram className="text-sm sm:text-base" />
            </motion.a>
            <motion.a
              variants={socialItem}
              href="https://www.youtube.com/@akrsyariahtv3737"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="flex items-center justify-center rounded-full bg-white/80 hover:bg-red-600 text-red-600 hover:text-white w-8 h-8 sm:w-9 sm:h-9 transition-colors"
            >
              <FaYoutube className="text-sm sm:text-base" />
            </motion.a>
          </motion.div>
          <motion.a
            variants={socialItem}
            href="https://play.google.com/store/apps/details?id=com.akrsyariahmobile"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-block w-full sm:w-auto"
          >
            <img
              src="/logo/playstore_akr.png"
              alt="Download di Play Store"
              className="w-1/2 p-0"
              loading="lazy"
            />
          </motion.a>
        </div>

        {/* TENTANG KAMI */}
        <div>
          <h3 className="text-sm font-semibold uppercase mb-3">Tentang Kami</h3>
          <ul className="space-y-2 text-sm">
            {tentangKami.map((item) => (
              <li key={item.link}>
                <Link to={item.link} className="hover:text-white">
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* PRODUK & LAYANAN */}
        <div>
          <h3 className="text-sm font-semibold uppercase mb-3">Produk & Layanan</h3>
          <ul className="space-y-2 text-sm">
            {services.map((service) => (
              <li key={service.id}>
                <Link to={`/produk-layanan/${service.slug}`} className="hover:text-white">
                  {service.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* KONTAK */}
        <div>
          <h3 className="text-xs md:text-sm font-semibold uppercase tracking-wide mb-3">
            Hubungi Kami
          </h3>
          <ul className="space-y-3 text-xs md:text-sm">
            <li>☎️ Telepon : (021) 599-1385</li>
            <li>
              ✉️ Email :{' '}
              <a
                href="https://mail.google.com/mail/?view=cm&to=kopsyahakr@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
                className="relative inline-block text-white hover:text-slate-300 transition-colors duration-200
                  after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:h-[1px] after:w-0
                  after:bg-current after:transition-all after:duration-300 hover:after:w-full"
              >
                kopsyahakr@gmail.com
              </a>
            </li>
            <li>
              📍 Alamat :{' '}
              <a
                href="https://www.google.com/maps/search/?api=1&query=PWS+Blok+AN+31+Gg.+Kav.+No.19-20+Kadu+Agung+Tigaraksa+Tangerang+Banten+15131"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 text-white hover:text-slate-300 transition-colors duration-200"
              >
                PWS Blok AN 31, Gg. Kav. No.19-20, Kadu Agung, Kec. Tigaraksa, Kab. Tangerang,
                Banten 15131
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* FOOTER BOTTOM */}
      <div className="border-t border-white/10 mt-10 pt-4 text-xs flex justify-between">
        <p>&copy; {new Date().getFullYear()} Koperasi AKR Syariah</p>
        <Link to="/kebijakan-privasi" className="hover:text-white">
          Kebijakan Privasi
        </Link>
      </div>
    </footer>
  );
}
